import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import { createPortal } from 'react-dom';

import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';

import styles from './styles.scss';

export default function Modal({
  children,
  hide,
  closeOnClickOutside = true,
  showCloseIcon = true,
  closeOnEscape = true,
  title = ' ',
  theme,
  className = '',
  headerClassName = '',
  titleClassName = '',
  contentClassName = '',
  ariaLabel,
  closeLabel = 'Close',
  trapFocus = false,
  returnFocusRef,
}) {
  const dialogRef = useRef(null);
  const hideRef = useRef(hide);
  const closeOnEscapeRef = useRef(closeOnEscape);
  const titleIdRef = useRef(`modal-title-${Math.random().toString(36).slice(2)}`);

  hideRef.current = hide;
  closeOnEscapeRef.current = closeOnEscape;

  const hideModal = () => {
    if (typeof hideRef.current === 'function') hideRef.current();
  };

  const closeModalOnClickOutside = () => {
    if (closeOnClickOutside) {
      hideModal();
    }
  };

  useEffect(() => {
    const previouslyFocusedElement = document.activeElement;
    const dialog = dialogRef.current;

    const closeModalByPressingEsc = (event) => {
      if (event.key === 'Escape' && closeOnEscapeRef.current) hideModal();

      if (!trapFocus || event.key !== 'Tab' || !dialog) return;

      const focusableElements = dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusableElements.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;
      const focusIsOutsideDialog = !dialog.contains(activeElement);

      if (event.shiftKey && (activeElement === firstElement || activeElement === dialog || focusIsOutsideDialog)) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        (activeElement === lastElement || activeElement === dialog || focusIsOutsideDialog)
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', closeModalByPressingEsc);
    if (trapFocus && dialog) dialog.focus();

    return () => {
      document.removeEventListener('keydown', closeModalByPressingEsc);
      const requestedFocusTarget = returnFocusRef && returnFocusRef.current;
      const focusTarget =
        requestedFocusTarget && document.contains(requestedFocusTarget)
          ? requestedFocusTarget
          : previouslyFocusedElement;

      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus();
      }
    };
  }, []);

  useLockBodyScroll();

  return (
    <>
      {createPortal(
        <>
          <div
            className={cx(styles['modal-overlay'], styles[`modal-overlay-${theme}`])}
            aria-hidden="true"
            onClick={closeModalOnClickOutside}
          />
          <div
            ref={dialogRef}
            className={cx(styles['modal-container'], styles[`modal-container-${theme}`], className)}
            aria-label={ariaLabel}
            aria-labelledby={title ? titleIdRef.current : undefined}
            aria-modal="true"
            tabIndex={-1}
            role="dialog">
            <div className={cx(styles['modal-header'], headerClassName)}>
              {title && (
                <span
                  id={titleIdRef.current}
                  className={cx(styles['modal-title'], styles[`modal-title-${theme}`], titleClassName)}>
                  {title}
                </span>
              )}

              {showCloseIcon && (
                <button
                  type="button"
                  className={styles['modal-closeButton']}
                  data-dismiss="modal"
                  aria-label={closeLabel}
                  onClick={hideModal}>
                  <span aria-hidden="true">&times;</span>
                </button>
              )}
            </div>

            <div className={cx(styles['modal-content'], styles[`modal-content-${theme}`], contentClassName)}>
              {children}
            </div>
          </div>
        </>,
        document.getElementById('modal-root'),
      )}
    </>
  );
}
